// API services for geocoding, routing, and Mapillary imagery

import { decode } from '../utils/polyline';

const MAPILLARY_ACCESS_TOKEN = process.env.REACT_APP_MAPILLARY_TOKEN || '';
const OSRM_DIRECTIONS_API = 'https://router.project-osrm.org/route/v1/driving/';
const MAPILLARY_API_BASE = 'https://graph.mapillary.com';

export interface MapillaryImage {
  id: string;
  thumbUrl: string;
  computedCompassAngle?: number;
  geometry: {
    coordinates: [number, number];
  };
}

/**
 * Convert address to lon,lat using Nominatim (OpenStreetMap geocoding)
 */
export async function geocodeAddress(address: string): Promise<string> {
  const nominatimUrl = 'https://nominatim.openstreetmap.org/search';
  const params = new URLSearchParams({
    q: address,
    format: 'json',
    limit: '1',
  });

  const response = await fetch(`${nominatimUrl}?${params}`, {
    headers: { 'User-Agent': 'MapillaryDrivingSimulator/1.0' },
  });

  if (!response.ok) {
    throw new Error(`Geocoding failed: ${response.statusText}`);
  }

  const result = await response.json();

  if (result && result.length > 0) {
    // Return in lon,lat format for OSRM
    return `${result[0].lon},${result[0].lat}`;
  } else {
    throw new Error(`Could not geocode address: ${address}`);
  }
}

/**
 * Get route coordinates between origin and destination using OSRM
 */
export async function getRouteCoordinates(
  origin: string,
  destination: string
): Promise<[number, number][]> {
  const url = `${OSRM_DIRECTIONS_API}${origin};${destination}?overview=full&geometries=polyline`;
  
  console.log('OSRM Request URL:', url);

  const response = await fetch(url);
  if (!response.ok) {
    const errorText = await response.text();
    console.error('OSRM Error Response:', errorText);
    throw new Error(`Route request failed: ${response.statusText}. Please check coordinates format.`);
  }

  const data = await response.json();
  console.log('OSRM Response:', data);

  if (data.code !== 'Ok') {
    throw new Error(`OSRM Error: ${data.message || 'Unknown routing error'}`);
  }

  if (data.routes && data.routes.length > 0) {
    const encodedPolyline = data.routes[0].geometry;
    return decode(encodedPolyline);
  }

  return [];
}

/**
 * Find Mapillary image near the given coordinate with specified heading
 */
export async function getMapillaryImage(
  coord: [number, number],
  heading: number
): Promise<MapillaryImage | null> {
  try {
    const [lat, lon] = coord;

    const url = `${MAPILLARY_API_BASE}/images`;
    const params = new URLSearchParams({
      access_token: MAPILLARY_ACCESS_TOKEN,
      fields: 'id,computed_compass_angle,geometry,captured_at,is_pano,thumb_2048_url',
      bbox: `${lon - 0.0002},${lat - 0.0002},${lon + 0.0002},${lat + 0.0002}`,
      limit: '50',
    });

    const response = await fetch(`${url}?${params}`);
    if (!response.ok) {
      throw new Error(`Mapillary API error: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.data && data.data.length > 0) {
      let bestImage: any = null;
      let bestScore = Infinity;

      for (const image of data.data) {
        // Skip panoramic images or images without thumb URL
        if (image.is_pano || !image.thumb_2048_url) continue;

        // Calculate distance
        let distance = 0.0001;
        if (image.geometry?.coordinates) {
          const [imgLon, imgLat] = image.geometry.coordinates;
          distance = Math.sqrt(
            Math.pow(imgLat - lat, 2) + Math.pow(imgLon - lon, 2)
          );

          // Distance threshold - only accept images within ~20 meters
          if (distance > 0.0002) continue;
        }

        // Calculate heading difference
        if (!image.computed_compass_angle) continue;

        let angleDiff = Math.abs(image.computed_compass_angle - heading);
        if (angleDiff > 180) {
          angleDiff = 360 - angleDiff;
        }

        // Only accept images within 45 degrees of desired heading
        if (angleDiff > 45) continue;

        // Combined score: prioritize heading match, then distance
        const score = angleDiff * 3 + distance * 10000;

        if (score < bestScore) {
          bestScore = score;
          bestImage = image;
        }
      }

      if (bestImage) {
        return {
          id: bestImage.id,
          thumbUrl: bestImage.thumb_2048_url,
          computedCompassAngle: bestImage.computed_compass_angle,
          geometry: bestImage.geometry,
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Mapillary API error:', error);
    return null;
  }
}

/**
 * Fetch multiple Mapillary images in parallel with batching to avoid overwhelming the API
 */
export async function getMapillaryImagesBatch(
  points: Array<{ coord: [number, number]; heading: number }>,
  batchSize: number = 10
): Promise<Array<MapillaryImage | null>> {
  const results: Array<MapillaryImage | null> = [];
  
  for (let i = 0; i < points.length; i += batchSize) {
    const batch = points.slice(i, i + batchSize);
    const batchPromises = batch.map(point => getMapillaryImage(point.coord, point.heading));
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Small delay between batches to be respectful to the API
    if (i + batchSize < points.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  return results;
}
